'use client';

import React, { memo, useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import AppImage from '@/components/ui/AppImage';
import { useCalendly } from '@/components/CalendlyPopup';

const navLinks = [
  { label: 'Our POV', href: '/#pov' },
  { label: 'Services', href: '/services' },
  { label: 'Approach', href: '/#how-we-build' },
  { label: 'About', href: '/#why-us' },
  { label: 'Blog', href: '/blog' },
];

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === '/';
  const { open: openCalendly } = useCalendly();

  useEffect(() => {
    if (!isHome) return;

    let lastScrolled = window.scrollY > 12;
    setScrolled(lastScrolled);

    const handleScroll = () => {
      const nextScrolled = window.scrollY > 12;
      if (nextScrolled !== lastScrolled) {
        lastScrolled = nextScrolled;
        setScrolled(nextScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((open) => !open), []);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b ${
          isHome
            ? `transition-[background-color,border-color] duration-300 ${
                scrolled ? 'border-white/10 bg-[#0F1117]' : 'border-transparent bg-[#0F1117]/92'
              }`
            : 'border-white/10 bg-[#0F1117]'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-[4.5rem] lg:px-8">
          <Link href="/" className="flex items-center gap-2.5" aria-label="Creator Tech Home">
            <span className="relative h-8 w-8 overflow-hidden rounded-md">
              <AppImage
                src="/assets/images/Screenshot_2026-04-27_at_12.11.59_AM-1777716156282.png"
                alt=""
                width={32}
                height={32}
                className="h-full w-full object-cover"
                priority
              />
            </span>
            <span className="text-[15px] font-semibold tracking-tight text-white">
              Creator Tech
            </span>
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Main navigation">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-white transition-colors duration-200 hover:text-[#2563FF]"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openCalendly}
              className="hidden items-center gap-2 rounded-full bg-[#2563FF] px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-[#2203C5] md:inline-flex"
            >
              Start a Project
              <span aria-hidden="true">→</span>
            </button>
            <button
              type="button"
              onClick={toggleMenu}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white lg:hidden"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              <span className="relative block h-3.5 w-4">
                <span
                  className={`absolute left-0 h-px w-4 bg-white transition-transform duration-200 ${
                    menuOpen ? 'top-1.5 rotate-45' : 'top-0'
                  }`}
                />
                <span
                  className={`absolute left-0 top-1.5 h-px w-4 bg-white transition-opacity duration-200 ${
                    menuOpen ? 'opacity-0' : 'opacity-100'
                  }`}
                />
                <span
                  className={`absolute left-0 h-px w-4 bg-white transition-transform duration-200 ${
                    menuOpen ? 'top-1.5 -rotate-45' : 'top-3'
                  }`}
                />
              </span>
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 bg-[#0F1117] transition-[opacity,visibility] duration-300 lg:hidden ${
          menuOpen ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
        aria-hidden={!menuOpen}
      >
        <nav className="flex h-full flex-col justify-end px-6 pb-10 pt-24" aria-label="Mobile navigation">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              onClick={closeMenu}
              className="border-t border-white/10 py-4 text-3xl font-semibold tracking-tight text-white transition-colors duration-200 hover:text-[#2563FF]"
            >
              {link.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => {
              closeMenu();
              openCalendly();
            }}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#2563FF] px-5 py-3.5 text-sm font-medium text-white"
          >
            Start a Project
            <span aria-hidden="true">→</span>
          </button>
        </nav>
      </div>
    </>
  );
}

export default memo(Header);
